import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompanyServiceSelectOption {
  value: string;
  label: string;
  code?: string;
}

/**
 * Servicios ya configurados en la empresa (company_service_coin_limits).
 * GET {apiUrl}/selects/company-services?companyId={id}
 */
@Injectable({ providedIn: 'root' })
export class CompanyServicesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, CompanyServiceSelectOption[]>();
  private readonly endpointUrl = '/selects/company-services';

  getOptions(companyId: string): Observable<CompanyServiceSelectOption[]> {
    const id = companyId.trim();
    if (!id) return of([]);

    const cached = this.cache.get(id);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { companyId: id },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(id, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): CompanyServiceSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          code?: string;
          text?: string;
          label?: string;
          name?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
        code: item.code || '',
      }));
    } catch {
      return [];
    }
  }
}
